/**
 * Scroll Animations
 *
 * Tasteful, subtle GSAP ScrollTrigger animations on content within blocks.
 * Animates inner content only (not block wrappers) to avoid background-color
 * sliding effects. Uses stagger where it makes sense.
 *
 * Respects prefers-reduced-motion for accessibility.
 */

import { gsap, ScrollTrigger } from './gsap-init.js';

/** Viewport check - cached on load */
const IS_MOBILE = !window.matchMedia('(min-width: 1024px)').matches;
const HERO_LCP_SELECTOR = 'h1, h2, .wp-block-heading';

/** Animation config - keep values modest for a refined feel */
const CONFIG = {
	duration: 0.7,
	ease: 'power2.out',
	yOffset: IS_MOBILE ? 15 : 20,
	opacityFrom: 0,
	start: 'top 95%',
	end: 'bottom 5%',
	stagger: 0.08,
};

/** Parallax config: disabled on mobile to prevent overlapping content */
const PARALLAX_CONFIG = {
	yEnter: IS_MOBILE ? 0 : 30,
	yExit: IS_MOBILE ? 0 : -30,
	start: 'top bottom',
	end: 'bottom top',
	scrub: 1,
	enabled: !IS_MOBILE,
};

/** Elements to exclude from generic animations (blocks with custom animations) */
const EXCLUDE_SELECTORS = [
	'.block-hero',
	'.block-two-column-text-image',
	'.block-two-column-text-carousel',
	'.block-building-card',
	'.block-building-grid',
	'.block-project-card',
	'.block-project-grid',
	'.block-news-list',
	'.block-news-grid',
	'.block-news-other-news-grid',
	'.block-news-card',
	'.block-checklist',
	'.block-image-carousel',
	'.block-highlight-grid',
	'.block-highlight-card',
	'.block-contact-details',
	'.block-building-unit-details',
	'.site-header',
	'.site-header__top-bar',
	'.site-header__navigation-bar',
	'header.wp-block-template-part',
	'.hamburger-menu',
	'.mobile-button-wrapper',
	'.vzp-property-listing__filters-open',
	'.skip-link',
	'.wp-block-hotspot-image',
	'.block-hotspot-image',
	'.block-gallery',
	'.block-two-column-accordion-carousel',
	'.block-upcoming-event',
	'.vzp-property-listing',
];

/**
 * Block config: trigger selector → { contentSelector, stagger }
 * Content is animated; block wrapper stays put (no background slide).
 */
const BLOCK_CONFIG = [
	/* Two-column text-image: handled by initTwoColumnTextImage() below */
	/* Gallery: has its own CSS-based animation (is-initial-load + fadeInUp) */
	/* Building card: handled by initBuildingCardAnimation() below */
	/* Building grid: handled by initBuildingGridAnimation() below */
	/* Highlight grid: handled by initHighlightGridAnimation() below */
	/* Standalone building card (exclude cards inside grid - those animate via grid) */
	{
		block: '.block-building-card',
		content: '.block-building-card__inner',
		stagger: false,
		excludeIfInside: '.block-building-grid',
	},
	{
		block: '.block-project-card',
		content: '.block-project-card__inner',
		stagger: false,
		excludeIfInside: '.block-project-grid',
	},
	{
		block: '.block-news-card',
		content: '.block-news-card__inner',
		stagger: false,
		excludeIfInside: '.block-news-list, .block-news-grid, .block-news-other-news-grid',
	},
	/* Accordion: stagger items */
	{
		block: '.wp-block-accordion, .block-accordion',
		content: '.accordion__item',
		stagger: true,
	},
	/* Two-column accordion carousel: handled by initTwoColumnAccordionCarousel() below */
	/* Building by the numbers */
	{
		block: '.block-building-by-the-numbers',
		content: '.block-building-by-the-numbers__inner',
		stagger: false,
	},
	/* Contact details: handled by initContactDetailsAnimation() below */
	/* Static map */
	{
		block: '.block-static-map',
		content: '.block-static-map__inner, .block-static-map > *',
		stagger: false,
	},
	/* Building unit details: no GSAP animation */
	/* Timeline: handled by initTimelineAnimation() below */
	/* Checklist: handled by initChecklistAnimation() below */
	/* Highlight card: handled by initHighlightGridAnimation() (content not animated) */
	/* WordPress columns: stagger each column */
	{
		block: '.wp-block-columns',
		content: '.wp-block-column',
		stagger: true,
	},
	/* WordPress groups: animate direct children only (nested groups handle their own content) */
	{
		block: '.wp-block-group.alignfull',
		content: ':scope > .is-layout-constrained > *, :scope > *',
		stagger: true,
	},
	/* Generic wp-block-group (fallback) */
	{
		block: '.wp-block-group',
		content: ':scope > .is-layout-constrained > *, :scope > *',
		stagger: true,
	},
];

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if block should be excluded
 */
function isExcluded(block) {
	if (EXCLUDE_SELECTORS.some((sel) => block.closest(sel) || block.matches(sel))) {
		return true;
	}

	// ACF wrappers (e.g. .wp-block-acf-upcoming-event) sit above .block-* roots.
	return EXCLUDE_SELECTORS.some((sel) => block.querySelector?.(sel));
}

/** Elements that animate themselves - don't animate as group children */
const SELF_ANIMATING = [
	'.wp-block-group',
	'.wp-block-columns',
	'.block-building-grid',
	'.block-project-grid',
	'.block-news-list',
	'.block-news-grid',
	'.block-news-other-news-grid',
	'.block-highlight-grid',
	'.block-building-card',
	'.block-project-card',
	'.block-news-card',
	'.block-upcoming-event',
];

/** Block/section selectors used as ScrollTrigger for text parallax (broadest match first) */
const PARALLAX_TRIGGER_SELECTORS = [
	'.wp-block-group.alignfull',
	'.wp-block-group.has-full-width',
	'.block-two-column-text-image',
	'.block-two-column-text-carousel',
	'.block-building-card',
	'.block-building-grid',
	'.block-project-card',
	'.block-project-grid',
	'.block-news-card',
	'.block-news-list',
	'.block-news-grid',
	'.block-news-other-news-grid',
	'.block-highlight-grid',
	'.wp-block-columns',
	'[class^="block-"]',
	'.wp-block-group',
];

/** Text and button selectors for parallax - these get parallax ONLY (no fade-in) */
const PARALLAX_SELECTORS = [
	'.wp-block-heading',
	'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
	'p',
	'.wp-block-buttons',
	'.wp-block-button__link',
	'.wp-element-button',
];

/** Check if element is a parallax target (headings, paragraphs, buttons) */
function isParallaxElement(el) {
	return PARALLAX_SELECTORS.some((sel) => el.matches(sel));
}

/**
 * Prefer section-level triggers (alignfull / blocks). For nested groups, use the outermost
 * group so parallax scroll range matches the section, not a small inner wrapper.
 */
function getParallaxTrigger(el) {
	for (const sel of PARALLAX_TRIGGER_SELECTORS) {
		if (sel === '.wp-block-group') continue;
		const match = el.closest(sel);
		if (match) return match;
	}

	let outerGroup = null;
	let node = el.parentElement;
	while (node) {
		if (node.matches?.('.wp-block-group')) outerGroup = node;
		node = node.parentElement;
	}
	return outerGroup || el;
}

/**
 * Animate content elements with optional stagger
 */
function animateContent(block, contentSelector, useStagger) {
	const trigger = block;
	const targets = block.querySelectorAll(contentSelector);
	const validTargets = Array.from(targets).filter((el) => {
		// Skip excluded blocks and their children
		if (isExcluded(el)) return false;
		// Skip if inside another animated block's content
		const alreadyAnimated = el.closest('[data-scroll-content-animated]');
		if (alreadyAnimated && alreadyAnimated !== block) return false;
		// Skip blocks that animate their own content (avoid double animation)
		if (SELF_ANIMATING.some((sel) => el.matches(sel))) return false;
		// Skip headings, paragraphs, buttons - they get parallax only (avoids opacity conflict)
		if (isParallaxElement(el)) return false;
		return true;
	});

	if (!validTargets.length) return;

	block.setAttribute('data-scroll-content-animated', '');

	const animationProps = {
		opacity: CONFIG.opacityFrom,
		y: CONFIG.yOffset,
		duration: CONFIG.duration,
		ease: CONFIG.ease,
		immediateRender: true,
		scrollTrigger: {
			trigger,
			start: CONFIG.start,
			end: CONFIG.end,
			toggleActions: 'play none none none',
		},
		overwrite: 'auto',
	};

	if (useStagger && validTargets.length > 1) {
		gsap.from(validTargets, {
			...animationProps,
			stagger: CONFIG.stagger,
		});
	} else {
		gsap.from(validTargets, animationProps);
	}
}

/**
 * Parallax for text and buttons: move faster out of view, slower into view
 * Uses the block/section as trigger for longer scroll range and smoother effect
 */
function initParallaxContent() {
	if (prefersReducedMotion()) return;
	if (!PARALLAX_CONFIG.enabled) return;

	const contentArea = document.querySelector('main, .entry-content, .wp-block-post-content, [role="main"]');
	if (!contentArea) return;

	const selector = PARALLAX_SELECTORS.join(', ');
	const elements = contentArea.querySelectorAll(selector);

	// Dedupe: avoid animating both parent and child (e.g. p inside .wp-block-heading)
	const validElements = Array.from(elements).filter((el) => {
		if (isExcluded(el)) return false;
		// Skip elements inside the hotspot image block
		if (el.closest('.wp-block-hotspot-image')) return false;
		// Skip elements inside the checklist block (items animate as a whole)
		if (el.closest('.block-checklist')) return false;
		// Skip elements inside highlight cards (cards animate as a whole)
		if (el.closest('.block-highlight-card')) return false;
		// Skip elements inside contact details (sections animate as a whole)
		if (el.closest('.block-contact-details')) return false;
		// Skip if an ancestor matching our selectors exists (animate topmost only)
		const ancestor = el.parentElement?.closest(selector);
		return !ancestor || ancestor === el;
	});

	validElements.forEach((el) => {
		const trigger = getParallaxTrigger(el);

		gsap.fromTo(el,
			{ y: PARALLAX_CONFIG.yEnter, force3D: true },
			{
				y: PARALLAX_CONFIG.yExit,
				force3D: true,
				ease: 'none',
				scrollTrigger: {
					trigger,
					start: PARALLAX_CONFIG.start,
					end: PARALLAX_CONFIG.end,
					scrub: PARALLAX_CONFIG.scrub,
				},
				overwrite: 'auto',
			}
		);
	});
}

/**
 * Two-column text-image block animation
 * Text column children get hero-style parallax (staggered speeds on exit)
 * Image column fades in on scroll
 */
function initTwoColumnTextImage() {
	if (prefersReducedMotion()) return;

	const blocks = document.querySelectorAll('.block-two-column-text-image');
	blocks.forEach((block) => {
		const firstCol = block.querySelector('.wp-block-column:first-child');
		const lastCol = block.querySelector('.wp-block-column:last-child');
		if (!firstCol) return;

		// Text children: heading, paragraphs, buttons (works with display:contents on mobile too)
		const textChildren = Array.from(
			block.querySelectorAll('.wp-block-heading, p, .wp-block-buttons')
		).filter((el) => firstCol.contains(el) || el.closest('.wp-block-column:first-child'));

		if (textChildren.length) {
			textChildren.forEach((child, i) => {
				if (IS_MOBILE) {
					// Mobile: simple fade-in with small y offset, no exit parallax
					gsap.fromTo(child,
						{ y: 20, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							ease: 'none',
							scrollTrigger: {
								trigger: block,
								start: 'top 90%',
								end: 'top 60%',
								scrub: 0.6,
							},
						}
					);
				} else {
					// Desktop: full parallax enter + exit
					const enterOffset = i * 40 + 60;
					const exitSpeed = (textChildren.length - i) * 80;

					gsap.fromTo(child,
						{ y: enterOffset, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							ease: 'none',
							scrollTrigger: {
								trigger: block,
								start: 'top 95%',
								end: 'top 55%',
								scrub: 0.6,
							},
						}
					);

					gsap.fromTo(child,
						{ y: 0 },
						{
							y: -exitSpeed,
							ease: 'none',
							immediateRender: false,
							scrollTrigger: {
								trigger: block,
								start: 'top top',
								end: 'bottom top',
								scrub: 0.6,
							},
						}
					);
				}
			});
		}

		// Image column: scale + blur + fade (+ horizontal slide on desktop)
		if (lastCol) {
			const image = lastCol.querySelector('.wp-block-image');
			if (image) {
				if (IS_MOBILE) {
					// Mobile: scale from center center + blur + fade
					gsap.fromTo(image,
						{ scale: 0.9, filter: 'blur(3px)', opacity: 0.3 },
						{
							scale: 1,
							filter: 'blur(0px)',
							opacity: 1,
							ease: 'none',
							scrollTrigger: {
								trigger: image,
								start: 'top 90%',
								end: 'top 60%',
								scrub: 0.6,
							},
						}
					);
				} else {
					// Desktop: full scale + blur + fade + horizontal slide
					const isTextRight = block.classList.contains('text-position--right');
					const xFrom = isTextRight ? -30 : 30;

					gsap.fromTo(image,
						{ scale: 0.85, filter: 'blur(4px)', opacity: 0.3, x: xFrom },
						{
							scale: 1,
							filter: 'blur(0px)',
							opacity: 1,
							x: 0,
							ease: 'none',
							scrollTrigger: {
								trigger: block,
								start: 'top 90%',
								end: 'top 50%',
								scrub: 0.6,
							},
						}
					);
				}
			}
		}
	});
}

/**
 * Two-column text-carousel block animation
 * Mirrors two-column-text-image: text parallax + carousel scale/blur/fade.
 */
function initTwoColumnTextCarousel() {
	if (prefersReducedMotion()) return;

	const blocks = document.querySelectorAll('.block-two-column-text-carousel');
	blocks.forEach((block) => {
		const textCol = block.querySelector('.b2ctc-column--text');
		const carouselCol = block.querySelector('.b2ctc-column--carousel');
		if (!textCol) return;

		const textChildren = Array.from(
			block.querySelectorAll('.b2ctc-column--text__inner .wp-block-heading, .b2ctc-column--text__inner p, .b2ctc-column--text__inner .wp-block-buttons')
		);

		if (textChildren.length) {
			textChildren.forEach((child, i) => {
				if (IS_MOBILE) {
					gsap.fromTo(child,
						{ y: 20, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							ease: 'none',
							scrollTrigger: {
								trigger: block,
								start: 'top 90%',
								end: 'top 60%',
								scrub: 0.6,
							},
						}
					);
				} else {
					const enterOffset = i * 40 + 60;
					const exitSpeed = (textChildren.length - i) * 80;

					gsap.fromTo(child,
						{ y: enterOffset, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							ease: 'none',
							scrollTrigger: {
								trigger: block,
								start: 'top 95%',
								end: 'top 55%',
								scrub: 0.6,
							},
						}
					);

					gsap.fromTo(child,
						{ y: 0 },
						{
							y: -exitSpeed,
							ease: 'none',
							immediateRender: false,
							scrollTrigger: {
								trigger: block,
								start: 'top top',
								end: 'bottom top',
								scrub: 0.6,
							},
						}
					);
				}
			});
		}

		const carousel = carouselCol?.querySelector('.b2ctc-carousel-swiper');
		if (carousel) {
			if (IS_MOBILE) {
				gsap.fromTo(carousel,
					{ scale: 0.9, filter: 'blur(3px)', opacity: 0.3 },
					{
						scale: 1,
						filter: 'blur(0px)',
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: carousel,
							start: 'top 90%',
							end: 'top 60%',
							scrub: 0.6,
						},
					}
				);
			} else {
				const isTextRight = block.classList.contains('text-position--right');
				const xFrom = isTextRight ? -30 : 30;

				gsap.fromTo(carousel,
					{ scale: 0.85, filter: 'blur(4px)', opacity: 0.3, x: xFrom },
					{
						scale: 1,
						filter: 'blur(0px)',
						opacity: 1,
						x: 0,
						ease: 'none',
						scrollTrigger: {
							trigger: block,
							start: 'top 90%',
							end: 'top 50%',
							scrub: 0.6,
						},
					}
				);
			}
		}
	});
}

/**
 * Hero intro animation
 * Staggers children of .block-hero__content up from below on page load
 */
function initHeroAnimation() {
	if (prefersReducedMotion()) return;

	const hero = document.querySelector('.block-hero');
	const heroContent = document.querySelector('.block-hero__content');
	if (!hero || !heroContent) return;

	const children = Array.from(heroContent.querySelectorAll(':scope > *'));
	if (!children.length) return;

	// Intro: stagger fade-in from bottom on page load
	const introTl = gsap.timeline();
	introTl.from(children, {
		// Headings are the LCP element; fading them in delays LCP by the whole intro.
		opacity: (i, el) => (el.matches(HERO_LCP_SELECTOR) ? 1 : 0),
		y: IS_MOBILE ? 25 : 40,
		duration: 0.9,
		ease: 'power3.out',
		stagger: 0.12,
		delay: 0.2,
		onComplete: () => {
			// Set up parallax only after intro finishes so y values are clean at 0
			children.forEach((child, i) => {
				const speed = IS_MOBILE
					? (children.length - i) * 50
					: (children.length - i) * 150;

				gsap.fromTo(child,
					{ y: 0 },
					{
						y: -speed,
						ease: 'none',
						immediateRender: false,
						scrollTrigger: {
							trigger: hero,
							start: 'top top',
							end: '80% top',
							scrub: 0.6,
						},
					}
				);
			});
		},
	});
}

/**
 * Visible project cards only (hidden filter items stay out of stagger math).
 */
function getVisibleProjectGridCards(grid) {
	return Array.from(grid.querySelectorAll('.block-project-card')).filter((card) => {
		const item = card.closest('.project-grid-item');
		if (!item) {
			return true;
		}

		return !item.hidden && item.style.display !== 'none';
	});
}

/**
 * Kill ScrollTriggers and inline GSAP styles for one project grid.
 */
function clearProjectGridAnimations(grid) {
	ScrollTrigger.getAll().forEach((st) => {
		if (st.trigger && grid.contains(st.trigger)) {
			st.kill();
		}
	});

	grid.querySelectorAll('.block-project-card, .block-project-card *').forEach((el) => {
		gsap.killTweensOf(el);
		gsap.set(el, { clearProps: 'all' });
	});
}

/**
 * Cards already on screen should not wait for more scroll after a filter change.
 */
function isProjectGridCardInView(card) {
	const rect = card.getBoundingClientRect();
	return rect.top < window.innerHeight && rect.bottom > 0;
}

function finalizeProjectGridCardVisibility(card) {
	ScrollTrigger.getAll().forEach((st) => {
		if (st.trigger === card) {
			st.kill();
		}
	});

	gsap.killTweensOf(card);
	gsap.set(card, { opacity: 1, y: 0 });

	const img = card.querySelector('.block-project-card__image img');
	if (img) {
		gsap.killTweensOf(img);
		gsap.set(img, { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' });
	}

	const content = card.querySelector('.block-project-card__content');
	if (content) {
		Array.from(content.children).forEach((item) => {
			gsap.killTweensOf(item);
			gsap.set(item, { opacity: 1, y: 0 });
		});
	}
}

/**
 * Project grid animation
 * Per-card triggers with earlier start/end so cards are fully visible sooner.
 */
function animateProjectGrid(grid = null) {
	if (prefersReducedMotion()) return;

	const isTablet = window.matchMedia('(min-width: 768px)').matches;
	const isWide = window.matchMedia('(min-width: 1024px)').matches;
	const grids = grid ? [grid] : document.querySelectorAll('.block-project-grid');

	grids.forEach((gridEl) => {
		clearProjectGridAnimations(gridEl);

		const cards = getVisibleProjectGridCards(gridEl);
		if (!cards.length) return;

		let cols = 1;
		if (isWide) {
			cols = 3;
		} else if (isTablet) {
			cols = 2;
		}

		cards.forEach((card, i) => {
			const row = Math.floor(i / cols);
			const col = i % cols;
			const cardOffset = IS_MOBILE
				? (row * 10) + (col * 6) + 10
				: (row * 18) + (col * 10) + 18;

			gsap.fromTo(card,
				{ y: cardOffset, opacity: 0 },
				{
					y: 0,
					opacity: 1,
					ease: 'none',
					scrollTrigger: {
						trigger: card,
						start: 'top bottom',
						end: 'top 78%',
						scrub: 0.6,
					},
				}
			);

			const imageWrapper = card.querySelector('.block-project-card__image');
			const img = imageWrapper?.querySelector('img');
			if (img) {
				gsap.fromTo(img,
					{ scale: 1.1, filter: 'blur(3px)', opacity: 0 },
					{
						scale: 1,
						filter: 'blur(0px)',
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: card,
							start: 'top 98%',
							end: 'top 75%',
							scrub: 0.6,
						},
					}
				);
			}

			const content = card.querySelector('.block-project-card__content');
			if (!content) return;

			const items = Array.from(content.children).filter((el) => !el.matches('.tag, [data-scroll-animated]'));
			items.forEach((item, itemIndex) => {
				const enterOffset = IS_MOBILE ? (itemIndex * 6 + 10) : (itemIndex * 10 + 16);

				gsap.fromTo(item,
					{ y: enterOffset, opacity: 0 },
					{
						y: 0,
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: card,
							start: 'top 98%',
							end: 'top 72%',
							scrub: 0.6,
						},
					}
				);
			});
		});

		requestAnimationFrame(() => {
			ScrollTrigger.refresh();
			cards.forEach((card) => {
				if (isProjectGridCardInView(card)) {
					finalizeProjectGridCardVisibility(card);
				}
			});
			ScrollTrigger.refresh();
		});
	});
}

function initProjectGridAnimation() {
	animateProjectGrid();
}

/**
 * Building grid animation
 * Cards in a grid slide up staggered, tied to scroll position
 */
function initBuildingGridAnimation() {
	if (prefersReducedMotion()) return;

	const grids = document.querySelectorAll('.block-building-grid, .block-news-list, .block-news-grid, .block-news-other-news-grid');
	grids.forEach((grid) => {
		const cards = Array.from(grid.querySelectorAll('.block-building-card, .block-project-card, .block-news-card'));
		
		if (cards.length) {
			// Each card gets its own scroll-tied animation with staggered offsets
			cards.forEach((card, i) => {
				const offset = IS_MOBILE ? (i * 20 + 30) : (i * 40 + 60);
				
				gsap.fromTo(card,
					{ y: offset, opacity: 0 },
					{
						y: 0,
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: grid,
							start: 'top 85%',
							end: 'top 45%',
							scrub: 0.6,
						},
					}
				);
			});
		}
	});
}

/**
 * Building card animation (all cards)
 * Image scales down from bottom, content items stagger into view
 */
function initBuildingCardAnimation() {
	if (prefersReducedMotion()) return;

	const cards = document.querySelectorAll('.block-building-card, .block-project-card, .block-news-card');
	cards.forEach((card) => {
		if (card.closest('.block-project-grid')) return;

		// Image animation: scale down from bottom, blurred, transparent
		const imageWrapper = card.querySelector('.block-building-card__image, .block-project-card__image, .block-news-card__image');
		if (imageWrapper) {
			const img = imageWrapper.querySelector('img');
			if (img) {
				gsap.fromTo(img,
					{ scale: 1.15, filter: 'blur(4px)', opacity: 0 },
					{
						scale: 1,
						filter: 'blur(0px)',
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: card,
							start: 'top 85%',
							end: 'top 55%',
							scrub: 0.6,
						},
					}
				);
			}
		}

		// Content animation: parallax scroll into view with staggered offsets
		const content = card.querySelector('.block-building-card__content, .block-project-card__content, .block-news-card__content');
		if (content) {
			const items = Array.from(content.children).filter((el) => {
				// Animate direct children (title, specs container, buttons)
				return !el.matches('.tag, [data-scroll-animated]');
			});

			if (items.length) {
				items.forEach((item, i) => {
					const enterOffset = IS_MOBILE ? (i * 15 + 25) : (i * 30 + 50);

					// Parallax enter: content rises into view from below, staggered
					gsap.fromTo(item,
						{ y: enterOffset, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							ease: 'none',
							scrollTrigger: {
								trigger: card,
								start: 'top 85%',
								end: 'top 50%',
								scrub: 0.6,
							},
						}
					);
				});
			}
		}
	});
}

/**
 * Checklist animation
 * Items stagger into view
 */
function initChecklistAnimation() {
	if (prefersReducedMotion()) return;

	const checklists = document.querySelectorAll('.block-checklist__list');
	checklists.forEach((list) => {
		const items = Array.from(list.querySelectorAll('.block-checklist__item'));
		
		if (items.length) {
			// Each item gets staggered parallax entry
			items.forEach((item, i) => {
				const offset = IS_MOBILE ? (i * 15 + 20) : (i * 30 + 40);
				
				gsap.fromTo(item,
					{ y: offset, opacity: 0 },
					{
						y: 0,
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: list,
							start: 'top 85%',
							end: 'top 50%',
							scrub: 0.6,
						},
					}
				);
			});
		}
	});
}

/**
 * Highlight grid animation
 * Cards stagger into view based on column layout
 * Mobile: 1 column, simple stagger
 * Desktop: 2/3/4 columns, row-by-row stagger with column offset
 * Content inside cards is NOT animated
 */
function initHighlightGridAnimation() {
	if (prefersReducedMotion()) return;

	const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

	const grids = document.querySelectorAll('.block-highlight-grid');
	grids.forEach((grid) => {
		const cards = Array.from(grid.querySelectorAll('.block-highlight-card'));
		if (!cards.length) return;

		// Determine column count based on viewport and grid class
		let cols = 1;
		if (isDesktop) {
			if (grid.classList.contains('desktop-columns-4')) cols = 4;
			else if (grid.classList.contains('desktop-columns-3')) cols = 3;
			else if (grid.classList.contains('desktop-columns-2')) cols = 2;
		}

		cards.forEach((card, i) => {
			// Row and column position
			const row = Math.floor(i / cols);
			const col = i % cols;

			// Base offset from row, plus small column offset for cascade effect
			const offset = IS_MOBILE
				? (i * 20 + 30)
				: (row * 50) + (col * 30) + 50;

			gsap.fromTo(card,
				{ y: offset, opacity: 0 },
				{
					y: 0,
					opacity: 1,
					ease: 'none',
					scrollTrigger: {
						trigger: grid,
						start: 'top 85%',
						end: 'top 45%',
						scrub: 0.6,
					},
				}
			);
		});
	});
}

/**
 * Two-column accordion carousel animation
 * Carousel side: scale + blur + fade + horizontal slide (mirrors two-column-text-image)
 * Accordion side: items stagger into view
 */
function initTwoColumnAccordionCarousel() {
	if (prefersReducedMotion()) return;

	const blocks = document.querySelectorAll('.block-two-column-accordion-carousel');
	blocks.forEach((block) => {
		// Accordion items: staggered parallax entry
		const accordionItems = Array.from(block.querySelectorAll('.accordion__item'));
		if (accordionItems.length) {
			accordionItems.forEach((item, i) => {
				const enterOffset = IS_MOBILE ? (i * 15 + 25) : (i * 40 + 60);

				gsap.fromTo(item,
					{ y: enterOffset, opacity: 0 },
					{
						y: 0,
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: block,
							start: 'top 95%',
							end: 'top 55%',
							scrub: 0.6,
						},
					}
				);
			});
		}

		// Carousel: scale + blur + fade + horizontal slide on desktop, simple fade on mobile
		const carousel = block.querySelector('.btcac-carousel-swiper');
		if (carousel) {
			if (IS_MOBILE) {
				gsap.fromTo(carousel,
					{ filter: 'blur(3px)', opacity: 0.3 },
					{
						filter: 'blur(0px)',
						opacity: 1,
						ease: 'none',
						scrollTrigger: {
							trigger: carousel,
							start: 'top 90%',
							end: 'top 60%',
							scrub: 0.6,
						},
					}
				);
			} else {
				const isAccordionRight = block.classList.contains('accordion-position--right');
				const xFrom = isAccordionRight ? -30 : 30;

				gsap.fromTo(carousel,
					{ scale: 0.85, filter: 'blur(4px)', opacity: 0.3, x: xFrom },
					{
						scale: 1,
						filter: 'blur(0px)',
						opacity: 1,
						x: 0,
						ease: 'none',
						scrollTrigger: {
							trigger: block,
							start: 'top 90%',
							end: 'top 50%',
							scrub: 0.6,
						},
					}
				);
			}
		}
	});
}

/**
 * Timeline animation
 * Entries stagger into view, then parallax upward when scrolling out at the top
 */
function initTimelineAnimation() {
	if (prefersReducedMotion()) return;

	const blocks = document.querySelectorAll('.block-timeline');
	blocks.forEach((block) => {
		const entries = Array.from(block.querySelectorAll('.block-timeline__entry'));
		if (!entries.length) return;

		entries.forEach((entry, i) => {
			const enterOffset = IS_MOBILE ? (i * 15 + 20) : (i * 30 + 40);

			gsap.fromTo(entry,
				{ y: enterOffset, opacity: 0, force3D: true },
				{
					y: 0,
					opacity: 1,
					force3D: true,
					ease: 'none',
					scrollTrigger: {
						trigger: block,
						start: 'top 95%',
						end: 'top 55%',
						scrub: 0.6,
					},
				}
			);

			if (!IS_MOBILE) {
				const exitSpeed = (entries.length - i) * 80;

				gsap.fromTo(entry,
					{ y: 0, force3D: true },
					{
						y: -exitSpeed,
						force3D: true,
						ease: 'none',
						immediateRender: false,
						overwrite: false,
						scrollTrigger: {
							trigger: block,
							start: 'top top',
							end: 'bottom top',
							scrub: 0.6,
						},
					}
				);
			}
		});
	});
}

/**
 * Contact details animation
 * Sections stagger into view, content inside is not animated separately
 */
function initContactDetailsAnimation() {
	if (prefersReducedMotion()) return;

	const blocks = document.querySelectorAll('.block-contact-details');
	blocks.forEach((block) => {
		const sections = Array.from(block.querySelectorAll('.block-contact-details__section'));
		if (!sections.length) return;

		sections.forEach((section, i) => {
			const offset = IS_MOBILE ? (i * 15 + 20) : (i * 30 + 40);

			gsap.fromTo(section,
				{ y: offset, opacity: 0 },
				{
					y: 0,
					opacity: 1,
					ease: 'none',
					scrollTrigger: {
						trigger: block,
						start: 'top 85%',
						end: 'top 50%',
						scrub: 0.6,
					},
				}
			);
		});
	});
}

/**
 * Initialize scroll animations
 */
function initScrollAnimations() {
	if (prefersReducedMotion()) return;
	if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

	const main = document.querySelector('main, .entry-content, .wp-block-post-content, [role="main"]');
	if (!main) return;

	BLOCK_CONFIG.forEach(({ block: blockSel, content, stagger, excludeIfInside }) => {
		const blocks = main.querySelectorAll(blockSel);

		blocks.forEach((block) => {
			if (isExcluded(block)) return;
			if (excludeIfInside && block.closest(excludeIfInside)) return;

			animateContent(block, content, stagger);
		});
	});

	initTwoColumnTextImage();
	initTwoColumnTextCarousel();
	initTwoColumnAccordionCarousel();
	initHeroAnimation();
	initProjectGridAnimation();
	initBuildingGridAnimation();
	initBuildingCardAnimation();
	initChecklistAnimation();
	initTimelineAnimation();
	initHighlightGridAnimation();
	initContactDetailsAnimation();
	initParallaxContent();

	// Nuclear cleanup: kill any GSAP tweens that ended up on checklist descriptions
	const checklistDescs = main.querySelectorAll('.block-checklist__item-description, .block-checklist__item-title');
	checklistDescs.forEach((el) => {
		gsap.killTweensOf(el);
		gsap.set(el, { clearProps: 'all' });
	});

	// Refresh ScrollTrigger after layout settles (images, fonts, etc.)
	window.addEventListener('load', () => {
		ScrollTrigger.refresh();
	});
	// Also refresh after a short delay for dynamic content
	requestAnimationFrame(() => requestAnimationFrame(() => ScrollTrigger.refresh()));
}

/**
 * Initialize when DOM is ready
 */
function init() {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initScrollAnimations);
	} else {
		initScrollAnimations();
	}
}

document.addEventListener('project-grid:filtered', (event) => {
	const grid = event.detail?.grid;
	if (grid) {
		animateProjectGrid(grid);
	}
});

init();
